import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Calculator,
  BookOpen,
  FileText,
  DollarSign,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  ChevronDown,
  RefreshCw,
  Loader2,
  Building2,
  CreditCard,
  Wallet,
  PiggyBank,
  Receipt,
  Plus,
  ExternalLink,
  BarChart3,
  CalendarDays,
  Download,
  Settings2,
  Scale,
  CalendarCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSessionState } from '@/hooks/use-session-state';
import { useTabState } from '@/hooks/use-tab-state';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { TradingAccountModal } from '@/components/trading/accounting/account-modal';
import { ExportCoaDialog } from '@/components/trading/accounting/export-coa-dialog';

type TabType = 'overview' | 'accounts' | 'journal' | 'rates';
const VALID_TABS: readonly TabType[] = ['overview', 'accounts', 'journal', 'rates'];

// TDMS Account Type Labels
const TDMS_TYPE_LABELS: Record<string, string> = {
  CURRENT_ASSET: 'Donen Varliklar',
  FIXED_ASSET: 'Duran Varliklar',
  SHORT_TERM_LIABILITY: 'Kisa Vadeli Yab. Kaynaklar',
  LONG_TERM_LIABILITY: 'Uzun Vadeli Yab. Kaynaklar',
  EQUITY: 'Oz Kaynaklar',
  INCOME: 'Gelirler',
  COST: 'Giderler/Maliyetler',
  FREE: 'Serbest',
  OFF_BALANCE: 'Nazim Hesaplar',
};

// TDMS Balance Type Labels
const BALANCE_TYPE_LABELS: Record<string, string> = {
  B: 'Borc (Debit)',
  A: 'Alacak (Credit)',
  C: 'Cift (Both)',
};

export function TradingAccountingPage() {
  const { t } = useTranslation('tradingAccounting');
  // Active tab lives in the `tab` URL query param (Back from a detail page
  // restores it) and is mirrored to sessionStorage so it also survives
  // navigating away via the sidebar and returning to the bare path.
  const [activeTab, setActiveTab] = useTabState<TabType>(
    'trading:accounting:activeTab',
    VALID_TABS,
    'overview'
  );

  const { data: accountStats, isLoading: isLoadingStats } = trpc.tradingAccount.stats.useQuery();
  const { data: journalStats, isLoading: isLoadingJournal } =
    trpc.tradingJournalEntry.stats.useQuery();
  const { data: accountTree, isLoading: isLoadingTree } = trpc.tradingAccount.getTree.useQuery({
    isActive: true,
  });

  const isLoading = isLoadingStats || isLoadingJournal || isLoadingTree;

  const utils = trpc.useUtils();

  const fetchTCMBMutation = trpc.exchangeRate.fetchFromKKTCMB.useMutation({
    onSuccess: () => {
      utils.exchangeRate.list.invalidate();
    },
  });

  // Recursively sum balances for an account and all its children — journal
  // lines post to leaf accounts only, parent groups carry no rolled-up balance.
  const sumTree = (node: { balance: number; children: typeof accountTree }): number => {
    const childrenSum = (node.children ?? []).reduce((s, c) => s + sumTree(c), 0);
    return Number(node.balance) + childrenSum;
  };

  const calculateTypeTotal = (type: string) => {
    return accountTree?.filter((a) => a.type === type).reduce((sum, a) => sum + sumTree(a), 0) || 0;
  };

  const totalCurrentAssets = calculateTypeTotal('CURRENT_ASSET');
  const totalFixedAssets = calculateTypeTotal('FIXED_ASSET');
  const totalAssets = totalCurrentAssets + totalFixedAssets;

  const totalShortTermLiabilities = Math.abs(calculateTypeTotal('SHORT_TERM_LIABILITY'));
  const totalLongTermLiabilities = Math.abs(calculateTypeTotal('LONG_TERM_LIABILITY'));
  const totalLiabilities = totalShortTermLiabilities + totalLongTermLiabilities;

  const totalEquity = Math.abs(calculateTypeTotal('EQUITY'));
  const totalIncome = Math.abs(calculateTypeTotal('INCOME'));
  const totalCosts = Math.abs(calculateTypeTotal('COST'));
  const netIncome = totalIncome - totalCosts;

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('title', 'Trading Accounting')}</h1>
          <p className="text-muted-foreground">
            {t('subtitle', 'TDMS compliant chart of accounts and journal entries')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/trading/accounting/posting-rules">
            <Button variant="outline" className="h-8">
              <Settings2 className="mr-2 h-4 w-4" />
              {t('postingRules', 'Entegrasyon Tanımları')}
            </Button>
          </Link>
          <Link to="/trading/accounting/tie-out">
            <Button variant="outline" className="h-8">
              <Scale className="mr-2 h-4 w-4" />
              {t('tieOut', 'Mutabakat')}
            </Button>
          </Link>
          <Link to="/trading/accounting/period-end">
            <Button variant="outline" className="h-8">
              <CalendarCheck className="mr-2 h-4 w-4" />
              {t('periodEnd', 'Dönem Sonu')}
            </Button>
          </Link>
          <Link to="/trading/accounting/reports">
            <Button variant="outline" className="h-8">
              <BarChart3 className="mr-2 h-4 w-4" />
              {t('financialReports', 'Financial Reports')}
            </Button>
          </Link>
          <Button
            variant="outline"
            className="h-8"
            onClick={() => fetchTCMBMutation.mutate()}
            disabled={fetchTCMBMutation.isPending}
          >
            {fetchTCMBMutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            {t('fetchTCMBRates', 'Fetch KKTC MB Rates')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b">
        <nav className="flex gap-4">
          {[
            { id: 'overview', labelKey: 'tabs.overview', icon: Calculator },
            { id: 'accounts', labelKey: 'tabs.accounts', icon: BookOpen },
            { id: 'journal', labelKey: 'tabs.journal', icon: FileText },
            { id: 'rates', labelKey: 'tabs.rates', icon: DollarSign },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as TabType)}
              className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'border-primary text-primary'
                  : 'text-muted-foreground hover:text-foreground border-transparent'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {t(tab.labelKey, tab.id.charAt(0).toUpperCase() + tab.id.slice(1))}
            </button>
          ))}
        </nav>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="space-y-6">
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="border-border bg-card border p-4">
                <div className="flex items-center gap-3">
                  <div className="bg-muted h-9 w-9 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="bg-muted h-5 w-24 animate-pulse" />
                    <div className="bg-muted h-3 w-16 animate-pulse" />
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="grid gap-6 lg:grid-cols-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="border-border bg-card border p-6">
                <div className="bg-muted mb-4 h-5 w-32 animate-pulse" />
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <div className="bg-muted h-8 w-20 animate-pulse" />
                    <div className="bg-muted h-8 w-20 animate-pulse" />
                  </div>
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="flex justify-between">
                      <div className="bg-muted h-4 w-24 animate-pulse" />
                      <div className="bg-muted h-4 w-12 animate-pulse" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Overview Tab */}
      {!isLoading && activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Financial Summary Cards */}
          <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-blue-100 p-2">
                  <Wallet className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalAssets, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('summary.totalAssets', 'Total Assets')}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-red-100 p-2">
                  <CreditCard className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalLiabilities, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('summary.liabilities', 'Liabilities')}
                  </p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-purple-100 p-2">
                  <Building2 className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalEquity, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.equity', 'Equity')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-green-100 p-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalIncome, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.income', 'Income')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className="bg-orange-100 p-2">
                  <TrendingDown className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(totalCosts, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">{t('summary.costs', 'Costs')}</p>
                </div>
              </div>
            </div>
            <div className="border-border bg-card border p-4">
              <div className="flex items-center gap-3">
                <div className={`p-2 ${netIncome >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                  <PiggyBank
                    className={`h-5 w-5 ${netIncome >= 0 ? 'text-green-600' : 'text-red-600'}`}
                  />
                </div>
                <div>
                  <p className="text-lg font-bold">{formatCurrency(netIncome, 'TRY')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('summary.netIncome', 'Net Income')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Row */}
          <div className="grid gap-6 md:grid-cols-2">
            {/* Account Stats */}
            {accountStats && (
              <div className="border-border bg-card border p-6">
                <h3 className="mb-4 text-lg font-semibold">
                  {t('chartOfAccounts.title', 'Chart of Accounts')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{accountStats.total}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('chartOfAccounts.totalAccounts', 'Total Accounts')}
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{accountStats.active}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('chartOfAccounts.active', 'Active')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.CURRENT_ASSET}</span>
                    <span className="font-medium">{accountStats.byType.CURRENT_ASSET}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.FIXED_ASSET}</span>
                    <span className="font-medium">{accountStats.byType.FIXED_ASSET}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.SHORT_TERM_LIABILITY}</span>
                    <span className="font-medium">{accountStats.byType.SHORT_TERM_LIABILITY}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.LONG_TERM_LIABILITY}</span>
                    <span className="font-medium">{accountStats.byType.LONG_TERM_LIABILITY}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.EQUITY}</span>
                    <span className="font-medium">{accountStats.byType.EQUITY}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.INCOME}</span>
                    <span className="font-medium">{accountStats.byType.INCOME}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{TDMS_TYPE_LABELS.COST}</span>
                    <span className="font-medium">{accountStats.byType.COST}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="mt-4 h-8 w-full"
                  onClick={() => setActiveTab('accounts')}
                >
                  {t('chartOfAccounts.viewAll', 'View All')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* Journal Entry Stats */}
            {journalStats && (
              <div className="border-border bg-card border p-6">
                <h3 className="mb-4 text-lg font-semibold">
                  {t('journalEntries.title', 'Journal Entries')}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{journalStats.total}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('journalEntries.totalEntries', 'Total Entries')}
                    </p>
                  </div>
                  <div className="bg-muted/50 p-3">
                    <p className="text-2xl font-bold">{journalStats.thisMonth}</p>
                    <p className="text-muted-foreground text-sm">
                      {t('journalEntries.thisMonth', 'This Month')}
                    </p>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-gray-400" />
                      {t('journalEntries.draft', 'Draft')}
                    </span>
                    <span className="font-medium">{journalStats.draft}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-green-400" />
                      {t('journalEntries.posted', 'Posted')}
                    </span>
                    <span className="font-medium">{journalStats.posted}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2">
                      <span className="h-2 w-2 bg-red-400" />
                      {t('journalEntries.voided', 'Voided')}
                    </span>
                    <span className="font-medium">{journalStats.voided}</span>
                  </div>
                </div>
                <Button
                  variant="outline"
                  className="mt-4 h-8 w-full"
                  onClick={() => setActiveTab('journal')}
                >
                  {t('journalEntries.viewAll', 'View All')}
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div className="border-border bg-card border p-6">
            <h3 className="mb-4 text-lg font-semibold">
              {t('quickActions.title', 'Quick Actions')}
            </h3>
            <div className="grid gap-4 md:grid-cols-4 lg:grid-cols-6">
              <Link
                to="/trading/accounting/journal-entries/new"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <FileText className="text-primary h-8 w-8" />
                <div>
                  <p className="font-medium">
                    {t('quickActions.newJournalEntry', 'New Journal Entry')}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.recordTransaction', 'Record Transaction')}
                  </p>
                </div>
              </Link>
              <Link
                to="/trading/accounting/reports"
                className="border-border hover:bg-muted/50 bg-primary/5 border-primary/20 flex items-center gap-3 border p-4 transition-colors"
              >
                <BarChart3 className="text-primary h-8 w-8" />
                <div>
                  <p className="font-medium">
                    {t('quickActions.financialReports', 'Financial Reports')}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.allReports', 'All Reports')}
                  </p>
                </div>
              </Link>
              <Link
                to="/trading/accounting/reports?tab=trial-balance"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <Receipt className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-medium">{t('quickActions.trialBalance', 'Trial Balance')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.viewBalances', 'View Balances')}
                  </p>
                </div>
              </Link>
              <Link
                to="/trading/accounting/reports?tab=profit-loss"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <TrendingUp className="h-8 w-8 text-blue-600" />
                <div>
                  <p className="font-medium">{t('quickActions.profitLoss', 'Profit & Loss')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.incomeStatement', 'Income Statement')}
                  </p>
                </div>
              </Link>
              <Link
                to="/trading/accounting/reports?tab=balance-sheet"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <Building2 className="h-8 w-8 text-purple-600" />
                <div>
                  <p className="font-medium">{t('quickActions.balanceSheet', 'Balance Sheet')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.financialPosition', 'Financial position')}
                  </p>
                </div>
              </Link>
              <Link
                to="/trading/accounting/periods"
                className="border-border hover:bg-muted/50 flex items-center gap-3 border p-4 transition-colors"
              >
                <CalendarDays className="h-8 w-8 text-cyan-600" />
                <div>
                  <p className="font-medium">{t('quickActions.fiscalPeriods', 'Fiscal Periods')}</p>
                  <p className="text-muted-foreground text-sm">
                    {t('quickActions.managePeriods', 'Manage Periods')}
                  </p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Chart of Accounts Tab */}
      {activeTab === 'accounts' && <AccountsTab />}

      {/* Journal Entries Tab */}
      {activeTab === 'journal' && <JournalEntriesTab />}

      {/* Exchange Rates Tab */}
      {activeTab === 'rates' && <ExchangeRatesTab />}
    </div>
  );
}

// Tree node type matching the API response
interface AccountTreeNode {
  id: string;
  code: string;
  name: string;
  type: string;
  balanceType: string;
  parentCode: string | null;
  balance: number;
  currency: string;
  isActive: boolean;
  isSystemAccount: boolean;
  children: AccountTreeNode[];
}

// Recursively filter tree nodes by search query
function filterTree(nodes: AccountTreeNode[], query: string): AccountTreeNode[] {
  const lower = query.toLowerCase();
  return nodes.reduce<AccountTreeNode[]>((acc, node) => {
    const matchesSelf =
      node.code.toLowerCase().includes(lower) || node.name.toLowerCase().includes(lower);
    const filteredChildren = filterTree(node.children, query);

    if (matchesSelf || filteredChildren.length > 0) {
      acc.push({ ...node, children: filteredChildren });
    }
    return acc;
  }, []);
}

// Recursively filter tree nodes by account type
function filterTreeByType(nodes: AccountTreeNode[], type: string): AccountTreeNode[] {
  return nodes.filter((node) => node.type === type);
}

// Collect all codes in a tree (for expand-all on search)
function collectAllCodes(nodes: AccountTreeNode[]): Set<string> {
  const codes = new Set<string>();
  for (const node of nodes) {
    if (node.children.length > 0) {
      codes.add(node.code);
      for (const code of collectAllCodes(node.children)) {
        codes.add(code);
      }
    }
  }
  return codes;
}

// Single tree row component
function AccountTreeRow({
  node,
  depth,
  expanded,
  onToggle,
  onNavigate,
  typeColors,
}: {
  node: AccountTreeNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (code: string) => void;
  onNavigate: (id: string) => void;
  typeColors: Record<string, string>;
}) {
  const hasChildren = node.children.length > 0;
  const isExpanded = expanded.has(node.code);
  const isLeaf = !hasChildren;

  return (
    <>
      <tr className="hover:bg-muted/30 group cursor-pointer" onClick={() => onNavigate(node.id)}>
        <td className="whitespace-nowrap px-4 py-1.5 font-mono text-sm">
          <span className="inline-flex items-center" style={{ paddingLeft: `${depth * 20}px` }}>
            {hasChildren ? (
              <button
                className="hover:bg-muted mr-1.5 flex-shrink-0 rounded p-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggle(node.code);
                }}
              >
                {isExpanded ? (
                  <ChevronDown className="text-muted-foreground h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="text-muted-foreground h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="mr-1.5 inline-block w-[22px]" />
            )}
            <span className={isLeaf ? 'text-foreground' : 'text-muted-foreground font-medium'}>
              {node.code}
            </span>
          </span>
        </td>
        <td className="px-4 py-1.5">
          <span className={isLeaf ? 'text-sm font-medium' : 'text-muted-foreground text-sm'}>
            {node.name}
          </span>
        </td>
        <td className="px-4 py-1.5">
          {depth === 0 && (
            <span className={`px-2 py-0.5 text-xs font-medium ${typeColors[node.type]}`}>
              {TDMS_TYPE_LABELS[node.type] || node.type}
            </span>
          )}
        </td>
        <td className="px-4 py-1.5 text-sm">
          <span className="font-mono">{node.balanceType}</span>
          <span className="text-muted-foreground ml-1 text-xs">
            ({BALANCE_TYPE_LABELS[node.balanceType]?.split(' ')[0] || node.balanceType})
          </span>
        </td>
        <td className="px-4 py-1.5 text-right font-mono text-sm">
          {node.balance !== 0 ? (
            <span className={node.balance < 0 ? 'text-red-600' : ''}>
              {formatCurrency(node.balance, node.currency)}
            </span>
          ) : (
            <span className="text-muted-foreground/40">-</span>
          )}
        </td>
        <td className="px-4 py-1.5 text-center">
          <ExternalLink className="text-muted-foreground h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
        </td>
      </tr>
      {hasChildren &&
        isExpanded &&
        node.children.map((child) => (
          <AccountTreeRow
            key={child.id}
            node={child}
            depth={depth + 1}
            expanded={expanded}
            onToggle={onToggle}
            onNavigate={onNavigate}
            typeColors={typeColors}
          />
        ))}
    </>
  );
}

// Recursively count all nodes (used for the export dialog hint)
function countNodes(nodes: AccountTreeNode[]): number {
  let total = 0;
  for (const n of nodes) {
    total += 1 + countNodes(n.children);
  }
  return total;
}

// Accounts Tab Component
function AccountsTab() {
  const { t } = useTranslation('tradingAccounting');
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useSessionState('trading:accounting:searchQuery', '');
  const [typeFilter, setTypeFilter] = useSessionState<string>('trading:accounting:typeFilter', '');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [isAccountModalOpen, setIsAccountModalOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const { data: chartPrep } = trpc.tradingAccount.chartPrepStatus.useQuery();
  const { data: treeData, isLoading } = trpc.tradingAccount.getTree.useQuery({ isActive: true });
  const tree: AccountTreeNode[] = (treeData as AccountTreeNode[] | undefined) ?? [];

  // Apply filters
  let filteredTree = tree;
  if (typeFilter) {
    filteredTree = filterTreeByType(filteredTree, typeFilter);
  }
  if (searchQuery.trim()) {
    filteredTree = filterTree(filteredTree, searchQuery.trim());
  }

  // Auto-expand all when searching
  const searchExpandedCodes = searchQuery.trim() ? collectAllCodes(filteredTree) : null;
  const effectiveExpanded = searchExpandedCodes ?? expanded;

  const toggleExpand = (code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) {
        next.delete(code);
      } else {
        next.add(code);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpanded(collectAllCodes(tree));
  };

  const collapseAll = () => {
    setExpanded(new Set());
  };

  const typeColors: Record<string, string> = {
    CURRENT_ASSET: 'bg-blue-100 text-blue-700',
    FIXED_ASSET: 'bg-blue-200 text-blue-800',
    SHORT_TERM_LIABILITY: 'bg-red-100 text-red-700',
    LONG_TERM_LIABILITY: 'bg-red-200 text-red-800',
    EQUITY: 'bg-purple-100 text-purple-700',
    INCOME: 'bg-green-100 text-green-700',
    COST: 'bg-orange-100 text-orange-700',
    FREE: 'bg-gray-100 text-gray-700',
    OFF_BALANCE: 'bg-gray-200 text-gray-800',
  };

  return (
    <div className="space-y-4">
      {chartPrep?.enabled && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3">
          <p className="text-sm font-medium text-amber-900">{t('chartPrep.title')}</p>
          <p className="mt-1 text-xs text-amber-800">{t('chartPrep.body')}</p>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <input
            type="text"
            placeholder={t('chartOfAccounts.searchPlaceholder', 'Search accounts...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 w-full max-w-sm border px-4 py-2 text-sm focus:outline-none focus:ring-1"
          />
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 min-w-[200px] cursor-pointer appearance-none border px-3 text-sm focus:outline-none focus:ring-1"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
            }}
          >
            <option value="">Tum Tipler</option>
            <option value="CURRENT_ASSET">1 - Donen Varliklar</option>
            <option value="FIXED_ASSET">2 - Duran Varliklar</option>
            <option value="SHORT_TERM_LIABILITY">3 - Kisa Vadeli Borc</option>
            <option value="LONG_TERM_LIABILITY">4 - Uzun Vadeli Borc</option>
            <option value="EQUITY">5 - Oz Kaynaklar</option>
            <option value="INCOME">6 - Gelirler</option>
            <option value="COST">7 - Maliyetler</option>
            <option value="FREE">8 - Serbest</option>
            <option value="OFF_BALANCE">9 - Nazim Hesaplar</option>
          </select>
          <div className="flex gap-1">
            <button
              onClick={expandAll}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded px-2 py-1 text-xs transition-colors"
            >
              Expand All
            </button>
            <button
              onClick={collapseAll}
              className="text-muted-foreground hover:text-foreground hover:bg-muted rounded px-2 py-1 text-xs transition-colors"
            >
              Collapse All
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="h-8" onClick={() => setIsExportDialogOpen(true)}>
            <Download className="mr-2 h-4 w-4" />
            {t('chartOfAccounts.export', 'Export')}
          </Button>
          <Button className="h-8" onClick={() => setIsAccountModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t('chartOfAccounts.newAccount', 'New Account')}
          </Button>
        </div>
      </div>

      {/* Accounts Tree Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : filteredTree.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <BookOpen className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">
            {t('chartOfAccounts.noAccountsFound', 'No accounts found')}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {searchQuery || typeFilter
              ? t('chartOfAccounts.adjustFilters', 'Try adjusting your filters')
              : t('chartOfAccounts.getStarted', 'Get started by creating an account')}
          </p>
          {!searchQuery && !typeFilter && (
            <Button className="mt-4 h-8" onClick={() => setIsAccountModalOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('chartOfAccounts.createAccount', 'Create Account')}
            </Button>
          )}
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('chartOfAccounts.code', 'Code')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('chartOfAccounts.name', 'Name')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('chartOfAccounts.type', 'Type')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('chartOfAccounts.balanceType', 'B/A/C')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium">
                  {t('chartOfAccounts.balance', 'Balance')}
                </th>
                <th className="w-12 px-4 py-2 text-center text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredTree.map((node) => (
                <AccountTreeRow
                  key={node.id}
                  node={node}
                  depth={0}
                  expanded={effectiveExpanded}
                  onToggle={toggleExpand}
                  onNavigate={(id) => navigate(`/trading/accounting/accounts/${id}`)}
                  typeColors={typeColors}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Account Modal */}
      <TradingAccountModal open={isAccountModalOpen} onOpenChange={setIsAccountModalOpen} />

      {/* Export Dialog */}
      <ExportCoaDialog
        open={isExportDialogOpen}
        onOpenChange={setIsExportDialogOpen}
        visibleTree={filteredTree}
        visibleCount={countNodes(filteredTree)}
      />
    </div>
  );
}

// Journal Entries Tab Component
function JournalEntriesTab() {
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useSessionState<string>(
    'trading:accounting:statusFilter',
    ''
  );
  const limit = 15;

  const { data, isLoading } = trpc.tradingJournalEntry.list.useQuery({
    page,
    limit,
    status: (statusFilter as 'DRAFT' | 'POSTED' | 'VOIDED') || undefined,
  });

  const entries = data?.entries ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 15, total: 0, totalPages: 0 };

  const statusColors: Record<string, string> = {
    DRAFT: 'bg-gray-100 text-gray-700',
    POSTED: 'bg-green-100 text-green-700',
    VOIDED: 'bg-red-100 text-red-700',
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex items-center justify-between gap-4">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border-border bg-background focus:border-foreground focus:ring-foreground/20 h-8 border px-3 text-sm focus:outline-none focus:ring-1"
        >
          <option value="">{t('journalEntries.allStatuses', 'All Statuses')}</option>
          <option value="DRAFT">{t('journalEntries.draft', 'Draft')}</option>
          <option value="POSTED">{t('journalEntries.posted', 'Posted')}</option>
          <option value="VOIDED">{t('journalEntries.voided', 'Voided')}</option>
        </select>
        <Link to="/trading/accounting/journal-entries/new">
          <Button className="h-8">
            <Plus className="mr-2 h-4 w-4" />
            {t('journalEntries.newEntry', 'New Entry')}
          </Button>
        </Link>
      </div>

      {/* Entries Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : entries.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <FileText className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">
            {t('journalEntries.noEntriesFound', 'No entries found')}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {statusFilter
              ? t('journalEntries.adjustFilter', 'Try adjusting your filter')
              : t('journalEntries.getStarted', 'Get started by creating an entry')}
          </p>
          {!statusFilter && (
            <Link to="/trading/accounting/journal-entries/new">
              <Button className="mt-4 h-8">
                <Plus className="mr-2 h-4 w-4" />
                {t('journalEntries.createEntry', 'Create Entry')}
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.entryNumber', 'Entry #')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.date', 'Date')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.description', 'Description')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.reference', 'Reference')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('journalEntries.status', 'Status')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium">
                  {t('journalEntries.amount', 'Amount')}
                </th>
                <th className="w-12 px-4 py-2 text-center text-sm font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {entries.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/trading/accounting/journal-entries/${entry.id}`)}
                >
                  <td className="px-4 py-2 font-mono text-sm">{entry.entryNumber}</td>
                  <td className="px-4 py-2 text-sm">
                    {new Date(entry.date).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-2">
                    <p className="font-medium">{entry.description}</p>
                    <p className="text-muted-foreground text-xs">
                      {entry.lines.length} {t('journalEntries.lines', 'lines')}
                    </p>
                  </td>
                  <td className="px-4 py-2 text-sm">
                    {entry.referenceType ? (
                      <span className="bg-muted px-2 py-0.5 text-xs">{entry.referenceType}</span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <span
                      className={`px-2 py-0.5 text-xs font-medium ${statusColors[entry.status]}`}
                    >
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-right font-mono">
                    {formatCurrency(entry.totalDebit, entry.currency)}
                  </td>
                  <td className="px-4 py-2 text-center">
                    <ExternalLink className="text-muted-foreground h-4 w-4" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t('pagination.previous', 'Previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {t('pagination.page', 'Page {{current}} of {{total}}', {
              current: page,
              total: pagination.totalPages,
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {t('pagination.next', 'Next')}
          </Button>
        </div>
      )}
    </div>
  );
}

// Exchange Rates Tab Component
function ExchangeRatesTab() {
  const { t, i18n } = useTranslation('tradingAccounting');
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const [page, setPage] = useState(1);
  const limit = 20;

  const { data, isLoading } = trpc.exchangeRate.list.useQuery({
    page,
    limit,
  });

  const utils = trpc.useUtils();

  const fetchTCMBMutation = trpc.exchangeRate.fetchFromKKTCMB.useMutation({
    onSuccess: () => {
      utils.exchangeRate.list.invalidate();
    },
  });

  const rates = data?.rates ?? [];
  const pagination = data?.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 };

  return (
    <div className="space-y-4">
      {/* Actions */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          className="h-8"
          onClick={() => fetchTCMBMutation.mutate()}
          disabled={fetchTCMBMutation.isPending}
        >
          {fetchTCMBMutation.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="mr-2 h-4 w-4" />
          )}
          {t('exchangeRates.fetchFromKKTCMB', 'Fetch from KKTC MB')}
        </Button>
      </div>

      {/* Rates Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="text-primary h-8 w-8 animate-spin" />
        </div>
      ) : rates.length === 0 ? (
        <div className="border-border bg-card border py-12 text-center">
          <DollarSign className="text-muted-foreground/50 mx-auto h-12 w-12" />
          <h3 className="mt-4 text-lg font-medium">
            {t('exchangeRates.noRatesFound', 'No rates found')}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm">
            {t('exchangeRates.clickToLoad', 'Click the button above to load rates')}
          </p>
        </div>
      ) : (
        <div className="border-border bg-card overflow-hidden border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.from', 'From')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.to', 'To')}
                </th>
                <th className="px-4 py-2 text-right text-sm font-medium">
                  {t('exchangeRates.rate', 'Rate')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.date', 'Date')}
                </th>
                <th className="px-4 py-2 text-left text-sm font-medium">
                  {t('exchangeRates.source', 'Source')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {rates.map((rate) => (
                <tr key={rate.id} className="hover:bg-muted/30">
                  <td className="px-4 py-2 font-mono text-sm">{rate.fromCurrency}</td>
                  <td className="px-4 py-2 font-mono text-sm">{rate.toCurrency}</td>
                  <td className="px-4 py-2 text-right font-mono">{rate.rate.toFixed(4)}</td>
                  <td className="px-4 py-2 text-sm">
                    {new Date(rate.date).toLocaleDateString(dateLocale)}
                  </td>
                  <td className="px-4 py-2">
                    <span className="bg-muted px-2 py-0.5 text-xs">{rate.source || 'MANUAL'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
          >
            {t('pagination.previous', 'Previous')}
          </Button>
          <span className="px-4 py-2 text-sm">
            {t('pagination.page', 'Page {{current}} of {{total}}', {
              current: page,
              total: pagination.totalPages,
            })}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8"
            onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages}
          >
            {t('pagination.next', 'Next')}
          </Button>
        </div>
      )}
    </div>
  );
}
